/** Engine ⇄ Cloudflare contract (docs/05-engine.md §3-4, §10; docs/06-cloudflare.md). Inbound frames are validated
 *  with zod; outbound payloads are plain types (the engine is trusted to produce them). */
import { z } from "zod";

export type Ms = number;

export const AccountStatus = z.enum(["active", "paused", "halted", "error", "closed"]);
export type AccountStatus = z.infer<typeof AccountStatus>;

export const EngineAccount = z.object({
  id: z.string().min(1).max(64),
  userId: z.string().min(1).max(64),
  name: z.string().max(80).default("Bot"),
  mode: z.enum(["paper", "live"]).default("paper"),
  strategyVersionId: z.string().min(1),       // e.g. "tpl:combined-usdt@1"
  pendingStrategyVersionId: z.string().optional(),
  status: AccountStatus.default("active"),
  riskScale: z.number().gt(0).max(1).default(1),
  /** paper: pretend money, orders filled against Binance's live order book and trade prints (realistic costs).
   *  paper-parity: fills at the model's bar-close prices with the backtest's cost assumption (engine == backtest).
   *  binance-demo: real orders on Binance Demo Trading. The strategy model is the same in every case. */
  exchange: z.enum(["paper", "paper-parity", "binance-demo", "binance"]).default("paper"),
  credentialId: z.string().optional(),
  capital: z.number().positive(),             // starting equity (paper) in quote currency
  startedAt: z.number().int().optional(),     // first bar the account trades (set by the engine)
});
export type EngineAccount = z.infer<typeof EngineAccount>;
export type EngineAccountInput = z.input<typeof EngineAccount>;

export const Command = z.discriminatedUnion("type", [
  z.object({ id: z.string(), type: z.literal("pause"), accountId: z.string() }),
  z.object({ id: z.string(), type: z.literal("resume"), accountId: z.string() }),
  z.object({ id: z.string(), type: z.literal("flatten"), accountId: z.string() }),
  z.object({ id: z.string(), type: z.literal("set_risk_scale"), accountId: z.string(), riskScale: z.number().gt(0).max(1) }),
  z.object({ id: z.string(), type: z.literal("switch_strategy"), accountId: z.string(), strategyVersionId: z.string() }),
  z.object({ id: z.string(), type: z.literal("admin_resume"), accountId: z.string() }),
  z.object({ id: z.string(), type: z.literal("close"), accountId: z.string() }),
  z.object({ id: z.string(), type: z.literal("kill_all"), flatten: z.boolean().default(false) }),
]);
export type Command = z.infer<typeof Command>;

export interface SignalSnapshot {
  modelId: string; barTime: Ms; createdAt: Ms;
  coins: string[];
  forecast: (number | null)[];
  volAnn: (number | null)[];
  expApr: (number | null)[];
  member: boolean[] | null;
  prices: { spot: (number | null)[]; perp: (number | null)[]; markHigh: (number | null)[] };
  funding: (number | null)[];
  flags: { stale: string[] };
  hash: string;
}

export type EngineEventKind =
  | "bar" | "rebalance" | "basis" | "throttle" | "kill_switch" | "paused" | "resumed" | "flattened"
  | "strategy_switched" | "risk_scale" | "account_added" | "account_closed" | "data_stale" | "error" | "trade";

export interface EngineEvent { t: Ms; accountId: string | null; kind: EngineEventKind; severity: "info" | "warn" | "critical"; text: string; data?: unknown }

export interface FillRow {
  id: string; accountId: string; t: Ms; symbol: string; market: "perp" | "spot"; book: "momentum" | "carry" | "rules" | "grid" | "hold";
  side: "buy" | "sell"; qty: number; price: number; notional: number; fee: number; positionAfter: number; reason: string;
}

export interface EquityRow { accountId: string; t: Ms; equity: number; wallet: number; basis: number; drawdown: number; grossLev: number; throttle: number }

export interface PositionView { symbol: string; momentum: number; carry: number; hold?: number }   // hold: BTC held by trend ($)

/** The real account on the exchange, next to what the model holds (demo/live bots). */
export interface VenueView {
  t: Ms; venue: "paper" | "binance-demo" | "binance";
  equity: number;                 // starting capital + what the bot made on the exchange (unrelated balances excluded)
  modelEquity: number; trackingPct: number;
  futures: { marginBalance: number; available: number; maintMargin: number };
  spotUsdt: number;
  positions: { symbol: string; market: "perp" | "spot"; qty: number; notional: number; targetQty: number }[];
  error: string | null;
}

/** One rebalance worked on the exchange: what it cost against the model's prices. */
export interface ExecutionSummary {
  planId: string; barTime: Ms; start: Ms; end: Ms;
  notional: number; feesUsdt: number; slippageUsdt: number; costBps: number; makerPct: number; assumedBps: number;
  legs: { symbol: string; market: "perp" | "spot"; side: "buy" | "sell"; qty: number; filledQty: number; avgPrice: number; modelPrice: number; slippageBps: number; feeUsdt: number; orders: number; error?: string }[];
}

/** Outbox frame types streamed to EngineHub. */
export type OutboxFrame =
  | { type: "equity"; accountId: string; payload: EquityRow }
  | { type: "fill"; accountId: string; payload: FillRow }
  | { type: "event"; accountId: string | null; payload: EngineEvent }
  | { type: "positions"; accountId: string; payload: { t: Ms; positions: PositionView[] } }
  | { type: "account"; accountId: string; payload: EngineAccount }
  | { type: "snapshot"; accountId: null; payload: { modelId: string; barTime: Ms; hash: string } }
  | { type: "command_result"; accountId: string | null; payload: { commandId: string; ok: boolean; message: string } }
  | { type: "venue"; accountId: string; payload: VenueView }
  | { type: "execution"; accountId: string; payload: ExecutionSummary };

/** Engine ⇄ EngineHub WebSocket frames (docs/05-engine.md §10). */
export interface LinkHello { t: "hello"; engineId: string; version: string; lastAckedSeq: number; ts: Ms; sig: string | null }
export interface LinkFrames { t: "frames"; items: { seq: number; type: OutboxFrame["type"]; accountId: string | null; payload: unknown }[] }
export interface LinkHeartbeat { t: "heartbeat"; ts: Ms; health: unknown }
export type EngineToHub = LinkHello | LinkFrames | LinkHeartbeat;

export const HubToEngine = z.discriminatedUnion("t", [
  z.object({ t: z.literal("ack"), seq: z.number().int().nonnegative() }),
  z.object({ t: z.literal("command"), command: Command }),
  z.object({ t: z.literal("account.upsert"), account: EngineAccount }),
  z.object({ t: z.literal("strategy.publish"), id: z.string().min(1), spec: z.unknown() }),
  z.object({ t: z.literal("ping"), ts: z.number() }),
]);
export type HubToEngine = z.infer<typeof HubToEngine>;
